// frontend/src/pages/Services.jsx
import React from "react";
import { Link } from "react-router-dom";
import SERVICES, { CITIES } from "../content/services";

// Map raw service names to homeowner-facing titles + descriptions + icon
function getDisplayInfo(rawName, idx) {
  const baseName = rawName || `Service ${idx + 1}`;
  const lower = baseName.toLowerCase();

  // Roofing
  if (lower.includes("roof")) {
    return {
      title: "Roofing services",
      badge: "Popular service",
      icon: "🏠",
      description:
        "Roof repairs, leaks, new roofs, and inspections. Tell us what’s going on and we’ll match you with a trusted roofing crew near you.",
    };
  }

  // Solar
  if (lower.includes("solar")) {
    return {
      title: "Solar services",
      badge: "Popular service",
      icon: "🔆",
      description:
        "Thinking about solar or need work on an existing system? Share your project details and BuildBright can help coordinate your request through available solar contractors in our network.",
    };
  }

  // Landscaping / yard / lawn
  if (lower.includes("landscap") || lower.includes("yard") || lower.includes("lawn")) {
    return {
      title: "Landscaping & yard care",
      badge: "Popular service",
      icon: "🌿",
      description:
        "Yard clean-ups, ongoing maintenance, tree trimming, irrigation fixes and more. We’ll match you with reliable crews that keep your property looking sharp.",
    };
  }

  // HVAC
  if (lower.includes("hvac") || lower.includes("ac") || lower.includes("heating")) {
    return {
      title: "HVAC services",
      badge: "Popular service",
      icon: "❄️",
      description:
        "AC repair, furnace installations, and maintenance. Share your project details and BuildBright can help coordinate your request through available HVAC contractors in our network.",
    };
  }

  // Plumbing
  if (lower.includes("plumb") || lower.includes("drain") || lower.includes("water heater")) {
    return {
      title: "Plumbing services",
      badge: "Popular service",
      icon: "🚰",
      description:
        "Drain cleaning, pipe repair, water heater installations, and leak detection. Find trusted local plumbers quickly.",
    };
  }

  // Electrical
  if (lower.includes("electric") || lower.includes("panel") || lower.includes("wiring")) {
    return {
      title: "Electrical services",
      badge: "Popular service",
      icon: "⚡",
      description:
        "Panel upgrades, EV chargers, lighting installation, and rewiring. Match with licensed electricians for safe, code-compliant work.",
    };
  }

  // Default for any other services in SERVICES
  return {
    title: baseName,
    badge: "Home service",
    icon: "🛠️",
    description:
      "Share a few project details and BuildBright can help organize your request for review by available local contractors.",
  };
}

export default function ServicesPage() {
  return (
    <main className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        {/* Hero */}
        <header className="mb-10 text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-orange-400/80">
            For homeowners
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold">
            Home services handled by top-tier pros.
          </h1>
          <p className="mt-3 max-w-2xl text-sm sm:text-base text-slate-300">
            Pick what you need help with and tell us where your home is.
            BuildBright helps organize your project request through available local contractors so you get
            fast quotes, zero runaround, and work done right the first time.
          </p>

          {/* Rating / trust mini-strip */}
          <div className="mt-4 inline-flex items-center gap-3 rounded-full border border-white/10 bg-black/5 px-4 py-2 text-xs sm:text-sm text-slate-200">
            <span className="inline-flex items-center gap-1">
              <span className="text-orange-300">★</span>
              <span>4.9 average rating</span>
            </span>
            <span className="h-3 w-px bg-zinc-700" />
            <span>Trusted by homeowners across the US</span>
          </div>
        </header>

        {/* Services cards – billionaire UI */}
        <section className="grid gap-6 md:grid-cols-3">
          {Array.isArray(SERVICES) &&
            SERVICES.map((s, idx) => {
              const rawName = s.name || s.title || s.label || `Service ${idx + 1}`;
              const info = getDisplayInfo(rawName, idx);

              return (
                <article
                  key={s.id || rawName || idx}
                  className="flex h-full flex-col justify-between rounded-2xl border border-white/10 bg-black/5 p-6 shadow-xl shadow-black/40 backdrop-blur-sm transition-transform transition-shadow duration-200 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/60"
                >
                  <div className="space-y-4">
                    <span className="inline-flex items-center rounded-full bg-orange-500/15 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-orange-200">
                      {info.badge}
                    </span>

                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-400/15 text-lg">
                        <span aria-hidden="true">{info.icon}</span>
                      </div>
                      <h2 className="text-lg font-semibold text-white">
                        {info.title}
                      </h2>
                    </div>

                    <p className="text-sm text-slate-300">{info.description}</p>
                  </div>

                  <div className="mt-6">
                    <Link
                      to="/get-quotes"
                      className="inline-flex w-full items-center justify-center rounded-xl bg-orange-400 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-orange-300"
                    >
                      Submit project request
                    </Link>
                  </div>
                </article>
              );
            })}
        </section>

        {/* Trust strip */}
        <section className="mt-10 rounded-2xl border border-white/10 bg-black/5 p-5 sm:p-6">
          <h2 className="text-sm sm:text-base font-semibold text-white">
            Why homeowners choose BuildBright
          </h2>
          <div className="mt-3 grid gap-3 text-xs sm:text-sm text-slate-200 sm:grid-cols-2">
            <div className="flex items-start gap-2">
              <span className="mt-[3px] text-orange-300">⚡</span>
              <p>
                <span className="font-medium">Fast matches.</span> Most projects
                request project support in minutes, not days.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-[3px] text-orange-300">✅</span>
              <p>
                <span className="font-medium">Contractor network.</span>{" "}
                We focus on project-fit review, service area, and available local contractors.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-[3px] text-orange-300">🔒</span>
              <p>
                <span className="font-medium">No spam, no selling your data.</span>{" "}
                Your request is reviewed for contractors relevant to your project type and service area.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-[3px] text-orange-300">🤝</span>
              <p>
                <span className="font-medium">You stay in control.</span> You
                choose who to talk to, when to schedule, and how to move
                forward.
              </p>
            </div>
          </div>
        </section>

        {/* Coverage + helper text */}
        <div className="mt-8 space-y-4">
          {Array.isArray(CITIES) && CITIES.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-black/5 px-4 py-4 sm:px-5 sm:py-5 text-xs sm:text-sm text-slate-200">
              <p className="font-semibold text-slate-50 mb-1">
                Serving homeowners nationwide
              </p>
              <p className="text-slate-300">
                Anywhere in the US – we help coordinate your project request through available local contractors in your area.
              </p>
            </div>
          )}

          <p className="text-[11px] text-zinc-400">
            Don&apos;t see your exact project listed? Choose the closest option
            above or{" "}
            <Link
              to="/get-quotes"
              className="text-orange-300 hover:text-orange-200 underline underline-offset-2"
            >
              request an estimate
            </Link>{" "}
            and add details in the notes. If it touches your roof, yard, or
            solar, there&apos;s a good chance we can help.
          </p>
        </div>

        {/* Bottom CTA */}
        <section className="mt-8 rounded-2xl border border-orange-400/40 bg-orange-400/10 px-5 py-6 sm:px-6 sm:py-7">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-orange-300">
                Ready to get started?
              </p>
              <h2 className="mt-1 text-base sm:text-lg font-semibold text-white">
                Tell us about your project and we&apos;ll match you with the
                right pro.
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-orange-100/90">
                It takes under two minutes. No spam. No obligation.
              </p>
            </div>
            <div className="flex-shrink-0">
              <Link
                to="/get-quotes"
                className="inline-flex items-center justify-center rounded-xl bg-orange-400 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-orange-300"
              >
                Start your estimate request
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
